import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Alert,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
  Autocomplete
} from '@mui/material';
import { 
  Delete as DeleteIcon, 
  Add as AddIcon, 
  ExpandMore as ExpandMoreIcon,
  Group as GroupIcon,
  Groups as GroupsIcon,
  Edit as EditIcon
} from '@mui/icons-material';
import axios from 'axios';
import { API_BASE_URL } from '../config/api.config';

const ProjectTeams = ({ projectId, projectName }) => {
  const [teams, setTeams] = useState([]);
  const [projectUsers, setProjectUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [openTeamDialog, setOpenTeamDialog] = useState(false);
  const [openMemberDialog, setOpenMemberDialog] = useState(false);
  const [openDeleteConfirmDialog, setOpenDeleteConfirmDialog] = useState(false);
  const [teamToDelete, setTeamToDelete] = useState(null);
  const [selectedTeam, setSelectedTeam] = useState(null);
  const [teamFormData, setTeamFormData] = useState({
    name: '',
    description: ''
  });
  const [memberFormData, setMemberFormData] = useState({
    user_id: ''
  });
  const [memberError, setMemberError] = useState('');
  // Edit team dialog state
  const [openEditTeamDialog, setOpenEditTeamDialog] = useState(false);
  const [editTeam, setEditTeam] = useState(null);
  const [editTeamData, setEditTeamData] = useState({ name: '', description: '' });
  const [editTeamError, setEditTeamError] = useState('');

  useEffect(() => {
    if (projectId) {
      fetchData();
    }
  }, [projectId]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [teamsResponse, projectUsersResponse] = await Promise.all([
        axios.get(`${API_BASE_URL}/teams?project_id=${projectId}`),
        axios.get(`${API_BASE_URL}/project-users?project_id=${projectId}`)
      ]);

      const teams = teamsResponse.data.teams || [];
      const users = projectUsersResponse.data.project_users || [];

      // Recupera i membri per ogni team
      const teamsWithMembers = await Promise.all(
        teams.map(async (team) => {
          try {
            const membersResponse = await axios.get(`${API_BASE_URL}/teams/${team.id}/members`);
            return {
              ...team,
              members: membersResponse.data.members || []
            };
          } catch (err) {
            console.error(`Error fetching members for team ${team.id}:`, err);
            return {
              ...team,
              members: []
            };
          }
        })
      );

      setTeams(teamsWithMembers);
      setProjectUsers(users);
    } catch (err) {
      console.error('Error fetching data:', err);
      
      // If it's a CORS or network error, show a more helpful message
      if (err.code === 'ERR_NETWORK' || err.message.includes('CORS')) {
        setError('Unable to connect to the backend server. The new project management features require the backend to be deployed with the latest changes.');
      } else {
        setError('Error loading project teams data');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleCreateTeam = async () => {
    try {
      await axios.post(`${API_BASE_URL}/teams`, {
        ...teamFormData,
        project_id: projectId
      });
      setOpenTeamDialog(false);
      setTeamFormData({ name: '', description: '' });
      fetchData();
    } catch (err) {
      setError('Error creating team');
      console.error('Error creating team:', err);
    }
  };

  const openEditDialog = (team) => {
    setEditTeam(team);
    setEditTeamData({ name: team.name || '', description: team.description || '' });
    setEditTeamError('');
    setOpenEditTeamDialog(true);
  };

  const handleUpdateTeam = async () => {
    if (!editTeam) return;
    try {
      await axios.put(`${API_BASE_URL}/teams?id=${editTeam.id}`, {
        name: editTeamData.name,
        description: editTeamData.description,
        project_id: editTeam.project_id || projectId
      });
      setOpenEditTeamDialog(false);
      setEditTeam(null);
      setEditTeamData({ name: '', description: '' });
      fetchData();
    } catch (err) {
      console.error('Error updating team:', err);
      if (err.response?.data?.error) {
        setEditTeamError(err.response.data.error);
      } else if (err.code === 'ERR_NETWORK' || (err.message && err.message.includes('CORS'))) {
        setEditTeamError('Unable to connect to the backend server. Please ensure the backend is deployed with the latest changes.');
      } else {
        setEditTeamError('Error updating team');
      }
    }
  };

  const handleDeleteTeam = async (teamId) => {
    try {
      const response = await axios.delete(`${API_BASE_URL}/teams?id=${teamId}`);
      console.log('Team deletion response:', response.data);
      setOpenDeleteConfirmDialog(false);
      setTeamToDelete(null);
      fetchData();
    } catch (err) {
      console.error('Error deleting team:', err);
      
      // If it's a CORS or network error, show a more helpful message
      if (err.code === 'ERR_NETWORK' || err.message.includes('CORS')) {
        setError('Unable to connect to the backend server. Please ensure the backend is deployed with the latest changes.');
      } else if (err.response?.data?.error) {
        setError(`Error deleting team: ${err.response.data.error}`);
      } else {
        setError('Error deleting team. The team may have members that need to be removed first.');
      }
    }
  };

  const confirmDeleteTeam = (team) => {
    setTeamToDelete(team);
    setOpenDeleteConfirmDialog(true);
  };

  const handleAddMember = async () => {
    try {
      await axios.post(`${API_BASE_URL}/teams/${selectedTeam.id}/members`, memberFormData);
      setOpenMemberDialog(false);
      setMemberFormData({ user_id: '' });
      setSelectedTeam(null);
      setMemberError('');
      fetchData();
    } catch (err) {
      console.error('Error adding member:', err);
      // Mostra l'errore direttamente nella popup del dialog
      if (err.response?.data?.error) {
        setMemberError(err.response.data.error);
      } else if (err.code === 'ERR_NETWORK' || (err.message && err.message.includes('CORS'))) {
        setMemberError('Unable to connect to the backend server. Please ensure the backend is deployed with the latest changes.');
      } else {
        setMemberError('Error adding member to team');
      }
    }
  };

  const handleRemoveMember = async (teamId, userId) => {
    try {
      await axios.delete(`${API_BASE_URL}/teams/${teamId}/members?user_id=${userId}`);
      fetchData();
    } catch (err) {
      console.error('Error removing member:', err);
      if (err.response?.data?.error) {
        setError(`Error removing member: ${err.response.data.error}`);
      } else if (err.code === 'ERR_NETWORK' || err.message.includes('CORS')) {
        setError('Unable to connect to the backend server. Please ensure the backend is deployed with the latest changes.');
      } else {
        setError('Error removing member from team');
      }
    }
  };

  const getRoleColor = (roleCode) => {
    const colors = {
      'S': '#1976d2', // Service Manager - Blue
      'J': '#388e3c', // JR Service Manager - Green
      'P': '#f57c00', // People Manager - Orange
      'T': '#7b1fa2', // Team Leader - Purple
      'C': '#757575'  // Team Member - Gray
    };
    return colors[roleCode] || '#757575';
  };

  const getAvailableUsersForTeam = (teamId) => {
    const team = teams.find(t => t.id === teamId);
    if (!team) return projectUsers;
    
    // Raccoglie tutti gli user_id che sono già membri di qualsiasi team del progetto
    const allMemberIds = teams.reduce((acc, t) => {
      if (t.members) {
        acc.push(...t.members.map(m => m.user_id));
      }
      return acc;
    }, []);
    
    // Filtra gli utenti che non sono già membri di nessun team del progetto
    return projectUsers.filter(user => !allMemberIds.includes(user.user_id));
  };

  if (loading) {
    return <Typography>Loading project teams...</Typography>;
  }

  return (
    <Box>
             <Box display="flex" justifyContent="flex-end" alignItems="center" mb={2}>
         <Button
           variant="contained"
           startIcon={<AddIcon />}
           onClick={() => setOpenTeamDialog(true)}
         >
           Add Team
         </Button>
       </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {teams.map((team) => (
        <Accordion key={team.id} sx={{ mb: 1 }}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Box display="flex" alignItems="center" justifyContent="space-between" width="100%">
                             <Box display="flex" alignItems="center">
                 <GroupsIcon sx={{ mr: 1 }} />
                 <Typography variant="h6">{team.name}</Typography>
                {team.members && (
                  <Chip 
                    label={`${team.members.length} members`} 
                    size="small" 
                    sx={{ ml: 2 }}
                  />
                )}
              </Box>
              <Box>
                <IconButton
                  size="small"
                  sx={{ color: 'grey.600', mr: 1 }}
                  onClick={(e) => {
                    e.stopPropagation();
                    openEditDialog(team);
                  }}
                >
                  <EditIcon />
                </IconButton>
                <IconButton
                  size="small"
                  sx={{ color: 'grey.600' }}
                  onClick={(e) => {
                    e.stopPropagation();
                    confirmDeleteTeam(team);
                  }}
                >
                  <DeleteIcon />
                </IconButton>
              </Box>
            </Box>
          </AccordionSummary>
          <AccordionDetails>
            <Box>
              <Typography variant="body2" color="textSecondary" mb={2}>
                {team.description || 'No description'}
              </Typography>
              
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                <Typography variant="subtitle1">Members</Typography>
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<AddIcon />}
                  onClick={() => {
                    setSelectedTeam(team);
                    setMemberError('');
                    setOpenMemberDialog(true);
                  }}
                >
                  Add Member
                </Button>
              </Box>

              {team.members && team.members.length > 0 ? (
                <TableContainer component={Paper} variant="outlined">
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>User</TableCell>
                        <TableCell>Role</TableCell>
                        <TableCell>Added Date</TableCell>
                        <TableCell>Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {team.members.map((member) => (
                        <TableRow key={member.user_id}>
                          <TableCell>
                            <Typography variant="body2">
                              {member.user?.username || 'Unknown User'}
                            </Typography>
                            <Typography variant="caption" color="textSecondary">
                              {member.user?.email || '-'}
                            </Typography>
                          </TableCell>
                                                     <TableCell>
                             <Chip
                               label={member.user?.role_name || '-'}
                               size="small"
                               sx={{
                                 backgroundColor: getRoleColor(member.user?.role_code),
                                 color: 'white'
                               }}
                             />
                           </TableCell>
                          <TableCell>
                            {member.created_at ? new Date(member.created_at).toLocaleDateString() : '-'}
                          </TableCell>
                          <TableCell>
                            <IconButton
                              size="small"
                              sx={{ color: 'grey.600' }}
                              onClick={() => handleRemoveMember(team.id, member.user_id)}
                            >
                              <DeleteIcon />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              ) : (
                <Typography color="textSecondary" align="center">
                  No members in this team
                </Typography>
              )}
            </Box>
          </AccordionDetails>
        </Accordion>
      ))}

      {teams.length === 0 && (
        <Paper sx={{ p: 3, textAlign: 'center' }}>
          <Typography color="textSecondary">
            No teams created for this project
          </Typography>
        </Paper>
      )}

      {/* Create Team Dialog */}
      <Dialog open={openTeamDialog} onClose={() => setOpenTeamDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Create New Team</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="Team Name"
            value={teamFormData.name}
            onChange={(e) => setTeamFormData({ ...teamFormData, name: e.target.value })}
            sx={{ mt: 1, mb: 2 }}
          />
          <TextField
            fullWidth
            label="Description"
            value={teamFormData.description}
            onChange={(e) => setTeamFormData({ ...teamFormData, description: e.target.value })}
            multiline
            rows={3}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenTeamDialog(false)}>Cancel</Button>
          <Button
            onClick={handleCreateTeam}
            variant="contained"
            disabled={!teamFormData.name}
          >
            Create Team
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add Member Dialog */}
      <Dialog open={openMemberDialog} onClose={() => { setOpenMemberDialog(false); setMemberError(''); }} maxWidth="sm" fullWidth>
        <DialogTitle>Add Member to Team</DialogTitle>
        <DialogContent>
          {memberError && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setMemberError('')}>
              {memberError}
            </Alert>
          )}
          {selectedTeam && getAvailableUsersForTeam(selectedTeam.id).length === 0 && (
            <Alert severity="info" sx={{ mb: 2 }}>
              No users available for this team. All project users are already assigned to other teams.
            </Alert>
          )}
          <Autocomplete
            options={selectedTeam ? getAvailableUsersForTeam(selectedTeam.id) : []}
            getOptionLabel={(option) => `${option.username} (${option.email}) - ${option.role_name}`}
            value={selectedTeam ? getAvailableUsersForTeam(selectedTeam.id).find(user => user.user_id === memberFormData.user_id) || null : null}
            onChange={(event, newValue) => {
              setMemberFormData({ user_id: newValue ? newValue.user_id : '' });
              if (memberError) setMemberError('');
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="User"
                required
                sx={{ mt: 1 }}
              />
            )}
            renderOption={(props, option) => (
              <Box component="li" {...props}>
                <Box>
                  <Typography variant="body2">{option.username}</Typography>
                  <Typography variant="caption" color="textSecondary">
                    {option.email} - {option.role_name}
                  </Typography>
                </Box>
              </Box>
            )}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenMemberDialog(false)}>Cancel</Button>
          <Button
            onClick={handleAddMember}
            variant="contained"
            disabled={!memberFormData.user_id}
          >
            Add Member
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Team Dialog */}
      <Dialog open={openEditTeamDialog} onClose={() => { setOpenEditTeamDialog(false); setEditTeamError(''); }} maxWidth="sm" fullWidth>
        <DialogTitle>Edit Team</DialogTitle>
        <DialogContent>
          {editTeamError && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setEditTeamError('')}>
              {editTeamError}
            </Alert>
          )}
          <TextField
            fullWidth
            label="Team Name"
            value={editTeamData.name}
            onChange={(e) => setEditTeamData({ ...editTeamData, name: e.target.value })}
            sx={{ mt: 1, mb: 2 }}
          />
          <TextField
            fullWidth
            label="Description"
            value={editTeamData.description}
            onChange={(e) => setEditTeamData({ ...editTeamData, description: e.target.value })}
            multiline
            rows={3}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setOpenEditTeamDialog(false); setEditTeamError(''); }}>Cancel</Button>
          <Button onClick={handleUpdateTeam} variant="contained" disabled={!editTeamData.name}>Save</Button>
        </DialogActions>
      </Dialog>

      {/* Delete Team Confirmation Dialog */}
      <Dialog open={openDeleteConfirmDialog} onClose={() => setOpenDeleteConfirmDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Confirm Team Deletion</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete the team "{teamToDelete?.name}"?
          </Typography>
          {teamToDelete?.members && teamToDelete.members.length > 0 && (
            <Alert severity="warning" sx={{ mt: 2 }}>
              This team has {teamToDelete.members.length} member(s). Deleting the team will also remove all team members.
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDeleteConfirmDialog(false)}>Cancel</Button>
          <Button
            onClick={() => handleDeleteTeam(teamToDelete?.id)}
            variant="contained"
            color="error"
          >
            Delete Team
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ProjectTeams; 